import { forwardRef } from 'react';

import { SvgIcon } from '@material-ui/core';
import type { SvgIconProps, Theme } from '@material-ui/core';
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';

import classNames from 'classnames';

import {
  CIRCLE_RADIUS,
  ICON_PADDING,
  ICON_VIEWBOX,
  STROKE_WIDTH,
  getColorsClasses,
} from './constants';
import { Color } from './types';

const CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS;

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: ICON_PADDING,
    },
    iconIncomplete: {
      strokeDasharray: '4 4',
    },
    '@keyframes dashFlow': {
      '0%': { strokeDashoffset: 0 },
      '100%': { strokeDashoffset: -CIRCUMFERENCE },
    },
    iconSpinning: {
      animation: '$dashFlow 3s linear infinite',
    },
    ...getColorsClasses(theme),
  });

type DraftProps = Omit<SvgIconProps, 'classes' | 'children' | 'color'> &
  WithStyles<typeof styles> & {
    color?: Color;
    incomplete?: boolean;
    spinning?: boolean;
  };

const DraftIcon = forwardRef<SVGSVGElement, DraftProps>((props, ref) => {
  const {
    classes,
    className,
    color: colorProp = 'info',
    incomplete,
    spinning,
    ...otherProps
  } = props;

  return (
    <SvgIcon
      ref={ref}
      viewBox={ICON_VIEWBOX}
      className={classNames(
        classes.root,
        classes[`${colorProp}Color`],
        className,
      )}
      {...otherProps}
    >
      <circle
        cx="12"
        cy="12"
        r={CIRCLE_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={STROKE_WIDTH}
        className={classNames({
          [classes.iconIncomplete]: incomplete,
          [classes.iconSpinning]: incomplete && spinning,
        })}
      />
    </SvgIcon>
  );
});

DraftIcon.displayName = 'StateIcon.DraftIcon';

export default withStyles(styles)(DraftIcon);
